import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, Lock, User, Loader2, Globe, MapPin } from 'lucide-react';

// List of countries for selection
const COUNTRIES = [
    "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria",
    "Bangladesh", "Belgium", "Brazil", "Canada", "Chile", "China", "Colombia",
    "Czech Republic", "Denmark", "Egypt", "Finland", "France", "Germany", "Greece",
    "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq",
    "Ireland", "Israel", "Italy", "Japan", "Jordan", "Kenya", "Kuwait", "Lebanon",
    "Libya", "Malaysia", "Mexico", "Morocco", "Nepal", "Netherlands", "New Zealand",
    "Nigeria", "Norway", "Oman", "Pakistan", "Peru", "Philippines", "Poland",
    "Portugal", "Qatar", "Romania", "Russia", "Saudi Arabia", "Singapore",
    "South Africa", "South Korea", "Spain", "Sri Lanka", "Sweden", "Switzerland",
    "Taiwan", "Thailand", "Turkey", "Ukraine", "United Arab Emirates",
    "United Kingdom", "United States of America", "Venezuela", "Vietnam"
].sort();

export const RegisterPage = () => {
    const navigate = useNavigate();
    const { register } = useAuth();
    
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (!selectedCountry) {
            setError('Please select your country');
            return;
        }

        setIsLoading(true);

        try {
            await register(email, username, password, 'domestic', selectedCountry);
            toast.success('Account created successfully!');
            navigate('/tracker');
        } catch (err) {
            console.error('Registration error:', err);
            setError(err.response?.data?.detail || 'Registration failed');
            toast.error('Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#071D35] flex items-center justify-center p-4 pt-20">
            <Card className="w-full max-w-md bg-[#1e293b]/50 backdrop-blur-md border-[#334155]">
                <CardHeader className="text-center">
                    <div className="flex items-center justify-center mb-4">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_181a1683-971b-4c0d-99ad-dbc6848d04b9/artifacts/2a99u289_AB%20Header%20Logo%20transparent.png"
                            alt="AirlineBound"
                            className="h-12 w-auto object-contain"
                        />
                    </div>
                    <CardTitle className="text-2xl font-bold text-white">Create Free Account</CardTitle>
                    <CardDescription className="text-[#94a3b8]">
                        Track flights in your selected country for free
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-[#94a3b8]">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 bg-[#071D35] border-[#334155] text-white placeholder:text-[#64748b] focus:border-[#f07050]"
                                    required
                                    data-testid="register-email-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="username" className="text-[#94a3b8]">Username</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                                <Input
                                    id="username"
                                    type="text"
                                    placeholder="Choose a username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="pl-10 bg-[#071D35] border-[#334155] text-white placeholder:text-[#64748b] focus:border-[#f07050]"
                                    required
                                    data-testid="register-username-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="country" className="text-[#94a3b8]">Your Country</Label>
                            <div className="relative">
                                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                                    <SelectTrigger 
                                        className="w-full bg-[#071D35] border-[#334155] text-white focus:border-[#f07050]"
                                        data-testid="register-country-select"
                                    >
                                        <div className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4 text-[#64748b]" />
                                            <SelectValue placeholder="Select your country..." />
                                        </div>
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#1e293b] border-[#334155] max-h-[250px]">
                                        {COUNTRIES.map(country => (
                                            <SelectItem 
                                                key={country} 
                                                value={country}
                                                className="text-white hover:bg-[#334155] cursor-pointer"
                                            >
                                                {country}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <p className="text-xs text-[#64748b]">
                                Free tier: Track flights in one country. Upgrade for multi-country access.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-[#94a3b8]">Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 bg-[#071D35] border-[#334155] text-white placeholder:text-[#64748b] focus:border-[#f07050]"
                                    required
                                    data-testid="register-password-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="text-[#94a3b8]">Confirm Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-10 bg-[#071D35] border-[#334155] text-white placeholder:text-[#64748b] focus:border-[#f07050]"
                                    required
                                    data-testid="register-confirm-password-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-[#f07050] hover:bg-[#e06040] text-white btn-primary-glow"
                            data-testid="register-submit-btn"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating account...
                                </>
                            ) : (
                                'Create Free Account'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-[#94a3b8] text-sm">
                            Already have an account?{' '}
                            <Link 
                                to="/login" 
                                className="text-[#f07050] hover:underline"
                                data-testid="register-login-link"
                            >
                                Sign in
                            </Link>
                        </p>
                    </div>

                    <div className="mt-4 p-3 rounded-lg bg-[#071D35]/50 border border-[#334155]">
                        <div className="flex items-start gap-2">
                            <Globe className="h-4 w-4 text-[#f07050] mt-0.5" />
                            <div>
                                <p className="text-xs text-[#94a3b8]">
                                    <span className="text-white font-medium">Free:</span> Track flights in your selected country (with ads)
                                </p>
                                <p className="text-xs text-[#94a3b8] mt-1">
                                    <span className="text-[#f07050] font-medium">Premium $4.99/mo:</span> Multi-country tracking, no ads
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
